import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { EventCard } from "../components/EventCard";
import { formatDateIt, zoneOf } from "../lib/constants";
import { Heart, Send, LogOut, KeyRound } from "lucide-react";
import { ChangePassword } from "../components/ChangePassword";
import { useNavigate } from "react-router-dom";

const STATUS_LABEL = { pending: "In attesa", approved: "Approvato", rejected: "Rifiutato" };
const STATUS_COLOR = { pending: "text-wyd-accent", approved: "text-green-400", rejected: "text-red-400" };

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("favorites");
  const [favorites, setFavorites] = useState([]);
  const [proposals, setProposals] = useState([]);

  useEffect(() => {
    api.get("/favorites").then((r) => setFavorites(r.data)).catch(() => {});
    api.get("/my-proposals").then((r) => setProposals(r.data)).catch(() => {});
  }, []);

  const toggleFav = async (id) => {
    try { await api.delete(`/favorites/${id}`); setFavorites((f) => f.filter((e) => e.id !== id)); } catch {}
  };

  return (
    <div className="pt-24 max-w-7xl mx-auto px-5 pb-16">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <div className="overline text-wyd-accent mb-2">Il mio profilo</div>
          <h1 className="font-display font-black text-4xl">{user?.name}</h1>
          <p className="text-wyd-muted mt-1">{user?.email}{user?.is_admin && " · Amministratore"}</p>
        </div>
        <button data-testid="profile-logout" onClick={() => { logout(); navigate("/"); }}
          className="self-start flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-wyd-muted hover:text-wyd-accent transition-colors duration-200 text-sm">
          <LogOut size={16} /> Esci
        </button>
      </div>

      <div className="flex gap-2 border-b border-white/10 mb-8">
        <Tab active={tab === "favorites"} onClick={() => setTab("favorites")} icon={<Heart size={16} />} testid="tab-favorites">Preferiti</Tab>
        <Tab active={tab === "proposals"} onClick={() => setTab("proposals")} icon={<Send size={16} />} testid="tab-proposals">Le mie proposte</Tab>
        <Tab active={tab === "security"} onClick={() => setTab("security")} icon={<KeyRound size={16} />} testid="tab-security">Sicurezza</Tab>
      </div>

      {tab === "security" ? (
        <ChangePassword />
      ) : tab === "favorites" ? (
        favorites.length === 0 ? (
          <Empty text="Nessun preferito ancora. Esplora gli eventi e salva quelli che ami!" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="favorites-grid">
            {favorites.map((e, i) => <EventCard key={e.id} event={e} index={i} isFavorite onToggleFavorite={toggleFav} />)}
          </div>
        )
      ) : proposals.length === 0 ? (
        <Empty text="Non hai ancora proposto eventi. Condividi la tua serata preferita!" />
      ) : (
        <div className="space-y-4" data-testid="proposals-list">
          {proposals.map((p) => (
            <div key={p.id} className="glass rounded-xl p-5 flex items-center justify-between gap-4">
              <div>
                <div className="font-display font-bold text-lg">{p.event_data.title}</div>
                <div className="text-sm text-wyd-muted mt-1">{[p.event_data.category, zoneOf(p.event_data), formatDateIt(p.event_data.date)].filter(Boolean).join(" · ")}</div>
              </div>
              <span className={`overline ${STATUS_COLOR[p.status]}`}>{STATUS_LABEL[p.status]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Tab = ({ active, onClick, children, icon, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors duration-200 ${
      active ? "border-wyd-accent text-white" : "border-transparent text-wyd-muted hover:text-white"
    }`}>
    {icon} {children}
  </button>
);

const Empty = ({ text }) => (
  <div className="text-center py-24 text-wyd-muted" data-testid="empty-state">{text}</div>
);
